/**
 * Vincent B939 (Eric 2026-02-14 SPEC STRICK PROD) — Correction Bulk Apply.
 *
 * Panneau qui liste toutes les corrections orthographiques uniques encore
 * en attente (ni acceptées, ni rejetées) sur la transcription fusionnée
 * courante, groupées par couple (original, correction). Vincent coche
 * plusieurs lignes puis clique « Accepter la sélection » ou « Rejeter
 * la sélection » — l'action est appliquée globalement via les endpoints
 * existants /accept-correction-global (B884) et /reject-correction-global
 * (B883). Chaque ligne montre aussi le nombre d'occurrences (segments
 * touchés) pour aider à prioriser.
 *
 * Compagnon du toggle B936 (« Appliquer partout » sur l'édition inline).
 * B936 = one correction, applied globally. B939 = many corrections, all
 * applied at once — utile en fin de session de review pour balayer les
 * dernières corrections évidentes.
 */
import { useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { SpinnerGap, CheckCircle, XCircle, Eye, ArrowLeft, Play } from "@phosphor-icons/react";
import { API_BASE as API } from "../../lib/apiBase";

export const PendingCorrectionsModal = ({
  open,
  onOpenChange,
  videoId,
  mergedSegments = [],
  onApplied,
  lang = "fr",
  // Tâche 1179 (Eric 2026-06-25) — Cliquer un extrait amène au segment et
  // lance la lecture. `seekTo` positionne la vidéo ET la joue
  // (VideoDetailPage). Absent (usage hors page vidéo) : les extraits restent
  // de simples textes, jamais de faux bouton qui ne fait rien.
  seekTo = null,
}) => {
  const [checked, setChecked] = useState(new Set()); // Set<"original||correction">
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, ko: 0 });
  // Vincent B941 (Eric 2026-02-14 SPEC STRICK PROD) — Filtres :
  //   • minLen  : longueur mini du mot original (utile pour ignorer les
  //               corrections de 1-2 caractères qui sont typiquement du bruit)
  //   • minOcc  : nb minimum d'occurrences (impact ≥ N)
  //   • pattern : regex libre appliquée à `original` (mode word-boundary
  //               si l'expression n'est pas déjà une regex — safe fallback
  //               via try/catch qui coupe en substring match).
  const [minLen, setMinLen] = useState(0);
  const [minOcc, setMinOcc] = useState(1);
  const [pattern, setPattern] = useState("");
  // Vincent B942 — Preview des segments impactés par une correction :
  // quand `preview` est un couple {original, correction}, on affiche
  // au-dessus de la liste les 2-3 premiers segments qui la contiennent
  // avec le contexte (30 chars avant/après).
  const [preview, setPreview] = useState(null);
  // Bogue 942 REFAIT (Vincent 2026-06-25) — « Avant d'appliquer les révisions
  // d'orthographe, montre un extrait de 2-3 segments avec le contexte ».
  // L'aperçu existait mais était caché derrière une petite icône par ligne, et
  // RIEN ne s'affichait au moment d'appliquer : Vincent appliquait donc à
  // l'aveugle. Désormais : un extrait toujours visible sur chaque ligne + une
  // étape de confirmation qui montre 2-3 extraits par correction sélectionnée.
  const [confirmStep, setConfirmStep] = useState(null); // null | "accept" | "reject"

  // Agrège les corrections pending par (original, correction) — un
  // segment peut porter plusieurs entrées, on ne compte que celles qui
  // ne sont NI accepted NI rejected.
  const pendingGroups = useMemo(() => {
    const map = new Map(); // key → { original, correction, occurrences, sampleSegmentIds }
    for (const s of mergedSegments || []) {
      for (const c of s.corrections || []) {
        if (c.accepted || c.rejected) continue;
        const key = `${c.original || ""}||${c.correction || ""}`;
        if (!map.has(key)) {
          map.set(key, { key, original: c.original || "", correction: c.correction || "", occurrences: 0, sampleSegmentIds: [] });
        }
        const g = map.get(key);
        g.occurrences += 1;
        // Vincent B942 — Garde jusqu'à 3 sample segments pour la preview.
        if (g.sampleSegmentIds.length < 3) g.sampleSegmentIds.push(s.id);
      }
    }
    let arr = Array.from(map.values()).sort((a, b) => b.occurrences - a.occurrences);
    // Vincent B941 — Applique les filtres (longueur, occurrences, regex).
    if (minLen > 0) arr = arr.filter((g) => (g.original || "").length >= minLen);
    if (minOcc > 1) arr = arr.filter((g) => g.occurrences >= minOcc);
    const trimmed = pattern.trim();
    if (trimmed) {
      let re = null;
      try { re = new RegExp(trimmed, "i"); } catch { re = null; }
      arr = arr.filter((g) => (re ? re.test(g.original || "") : (g.original || "").toLowerCase().includes(trimmed.toLowerCase())));
    }
    return arr;
  }, [mergedSegments, minLen, minOcc, pattern]);

  // Bogue 942 — Extraction des exemples avec contexte (30 caractères de part
  // et d'autre). Partagée par l'extrait de ligne, l'aperçu détaillé et l'étape
  // de confirmation : un seul calcul, donc aucun risque de divergence entre ce
  // qui est montré et ce qui sera appliqué.
  //
  // Bogue 942, 3e passe (Vincent 2026-06-25 : « Extraits » ne faisait rien) —
  // On cherchait `original` dans `merged_text`. Or la fusion TISSE DÉJÀ le mot
  // corrigé dans le texte (une correction en attente est un mot déjà présent
  // sous sa forme corrigée, surligné en « proposé »). Dans les données réelles
  // `original` est donc introuvable : zéro extrait, et le panneau — rendu sous
  // condition `previewSamples.length > 0` — ne s'affichait tout simplement pas.
  // On accepte désormais les deux formes, et on affiche toujours quelque chose.
  const samplesFor = (original, correction, limit = 3) => {
    const samples = [];
    for (const s of mergedSegments || []) {
      if (samples.length >= limit) break;
      // Le sélecteur fiable, c'est la correction portée par le segment — pas
      // une recherche de texte.
      const hasPending = (s.corrections || []).some(
        (c) => c.original === original && c.correction === correction && !c.accepted && !c.rejected,
      );
      if (!hasPending) continue;
      const txt = s.merged_text || "";
      let idx = txt.indexOf(original);
      let hit = original;
      if (idx < 0) {
        idx = txt.indexOf(correction);
        hit = correction;
      }
      if (idx < 0) {
        // Ni l'un ni l'autre : on montre quand même le début du segment,
        // plutôt que de faire disparaître l'extrait sans rien dire.
        samples.push({
          id: s.id,
          start: s.start,
          before: txt.slice(0, 60),
          original,
          correction,
          after: "",
          approx: true,
        });
        continue;
      }
      samples.push({
        id: s.id,
        start: s.start,
        before: txt.substring(Math.max(0, idx - 30), idx),
        original,
        correction,
        after: txt.substring(idx + hit.length, Math.min(txt.length, idx + hit.length + 30)),
      });
    }
    return samples;
  };

  const previewSamples = useMemo(
    () => (preview ? samplesFor(preview.original, preview.correction, 3) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [preview, mergedSegments],
  );

  const selectedGroups = pendingGroups.filter((g) => checked.has(g.key));

  // Tâche 1179 — Saut au segment avec lecture. On ferme la modale : rester
  // devant un panneau opaque pendant que l'audio joue derrière n'aide personne.
  const canJump = typeof seekTo === "function";
  const jumpToSample = (s) => {
    if (!canJump || s?.start == null) return;
    onOpenChange(false);
    setTimeout(() => {
      seekTo(Number(s.start));
      const row = document.querySelector(`[data-seg-id="${s.id}"]`);
      if (row) row.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);
  };

  const toggleCheck = (key) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const toggleAll = () => {
    if (checked.size === pendingGroups.length) setChecked(new Set());
    else setChecked(new Set(pendingGroups.map((g) => g.key)));
  };

  const bulkApply = async (action) => {
    if (checked.size === 0 || !videoId) return;
    const endpoint = action === "accept" ? "accept-correction-global" : "reject-correction-global";
    const label = action === "accept"
      ? (lang === "fr" ? "acceptées" : "accepted")
      : (lang === "fr" ? "rejetées" : "rejected");
    setSubmitting(true);
    setProgress({ done: 0, ko: 0 });
    let done = 0, ko = 0, totalSegs = 0;
    const targets = pendingGroups.filter((g) => checked.has(g.key));
    for (const g of targets) {
      try {
        const r = await axios.post(
          `${API}/api/videos/${videoId}/${endpoint}`,
          { original: g.original, correction: g.correction },
          { withCredentials: true },
        );
        totalSegs += r.data?.segments_updated ?? 0;
        done++;
      } catch {
        ko++;
      }
      setProgress({ done, ko });
    }
    setSubmitting(false);
    if (ko === 0) {
      toast.success(
        lang === "fr"
          ? `✅ ${done} correction(s) ${label} — ${totalSegs} segment(s) mis à jour.`
          : `✅ ${done} correction(s) ${label} — ${totalSegs} segment(s) updated.`,
      );
    } else {
      toast.warning(
        lang === "fr"
          ? `${done} OK · ${ko} erreur(s). Voir la console.`
          : `${done} OK · ${ko} error(s). See console.`,
      );
    }
    setChecked(new Set());
    setConfirmStep(null);
    if (typeof onApplied === "function") {
      try { await onApplied(); } catch { /* ignore */ }
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Bogue 1180 (Vincent 2026-06-25) — La fenêtre débordait très largement
          de l'écran et rendait « Accepter » / « Rejeter » inatteignables.
          Cause : les corrections sont des PHRASES entières (« Ben là, on parle
          tu d'assurance? ») affichées en `font-mono` sans retour à la ligne, et
          les conteneurs flex n'avaient pas le droit de rétrécir (pas de
          `min-w-0`) — le contenu poussait donc la largeur au lieu de passer à la
          ligne. Largeur bornée à l'écran + `overflow-hidden` ici, et retour à la
          ligne forcé sur chaque texte de correction ci-dessous. */}
      <DialogContent
        className="w-[min(92vw,900px)] max-w-[min(92vw,900px)] max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="pending-corrections-modal"
      >
        <DialogHeader>
          <DialogTitle>
            {lang === "fr" ? "Corrections orthographiques en attente" : "Pending spelling corrections"}
          </DialogTitle>
          <DialogDescription>
            {lang === "fr"
              ? `${pendingGroups.length} correction(s) unique(s) encore non traitée(s). Cochez celles à traiter en bloc puis choisissez « Accepter » ou « Rejeter ».`
              : `${pendingGroups.length} unique correction(s) still pending. Check items to batch, then Accept or Reject.`}
          </DialogDescription>
        </DialogHeader>

        {pendingGroups.length === 0 ? (
          <div className="py-8 text-center text-sm text-zinc-500" data-testid="pending-corrections-empty">
            {lang === "fr" ? "🎉 Aucune correction en attente." : "🎉 No pending corrections."}
            {(minLen > 0 || minOcc > 1 || pattern.trim()) && (
              <p className="text-[10px] mt-1 text-zinc-400">
                {lang === "fr" ? "(filtres actifs — essayez de les relâcher)" : "(active filters — try loosening them)"}
              </p>
            )}
          </div>
        ) : confirmStep ? (
          /* Bogue 942 (refait, Vincent 2026-06-25) — Étape OBLIGATOIRE avant
             d'appliquer : pour chaque correction sélectionnée, 2 à 3 extraits
             réels avec leur contexte. Vincent voit exactement ce qui change. */
          <div className="space-y-2" data-testid="pending-corrections-apply-preview">
            <p className={`text-xs font-semibold ${confirmStep === "accept" ? "text-teal-800" : "text-rose-800"}`}>
              {confirmStep === "accept"
                ? (lang === "fr"
                    ? `Voici ce qui va changer — ${selectedGroups.length} correction(s) à accepter :`
                    : `Here is what will change — ${selectedGroups.length} correction(s) to accept:`)
                : (lang === "fr"
                    ? `Voici ce qui restera inchangé — ${selectedGroups.length} correction(s) à rejeter :`
                    : `Here is what stays unchanged — ${selectedGroups.length} correction(s) to reject:`)}
            </p>
            <ul className="max-h-[min(50vh,380px)] min-w-0 overflow-y-auto overflow-x-hidden border border-zinc-200 rounded-lg divide-y divide-zinc-100">
              {selectedGroups.map((g) => {
                const samples = samplesFor(g.original, g.correction, 3);
                return (
                  <li key={g.key} className="px-3 py-2 space-y-1 min-w-0" data-testid={`pending-corrections-confirm-row-${g.key}`}>
                    <div className="flex items-baseline gap-2 flex-wrap text-[12px] min-w-0">
                      <span className="text-rose-700 line-through font-mono break-words min-w-0">{g.original}</span>
                      <span className="text-zinc-400">→</span>
                      <span className="text-teal-700 font-mono font-semibold break-words min-w-0">{g.correction}</span>
                      <span className="ml-auto flex-shrink-0 text-[10px] text-zinc-500 tabular-nums">
                        {lang === "fr" ? `${g.occurrences} segment(s)` : `${g.occurrences} segment(s)`}
                      </span>
                    </div>
                    {samples.length === 0 ? (
                      <p className="text-[10px] text-zinc-400 italic">
                        {lang === "fr"
                          ? "Aucun extrait disponible (le passage a été réécrit depuis la détection)."
                          : "No extract available (the passage was rewritten since detection)."}
                      </p>
                    ) : (
                      samples.map((s, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => jumpToSample(s)}
                          disabled={!canJump || s.start == null}
                          className={`block w-full min-w-0 text-left text-[11px] text-zinc-700 font-mono leading-snug break-words whitespace-normal rounded px-1 -mx-1 ${
                            canJump && s.start != null ? "hover:bg-blue-50 cursor-pointer" : "cursor-default"
                          }`}
                          data-testid={`pending-corrections-confirm-sample-${g.key}-${i}`}
                          title={canJump && s.start != null
                            ? (lang === "fr" ? "Écouter ce passage dans la vidéo" : "Listen to this passage in the video")
                            : undefined}
                        >
                          <span className="text-zinc-400">…{s.before}</span>
                          <span className="text-rose-700 line-through">{s.original}</span>
                          <span className="text-zinc-400 mx-0.5">→</span>
                          <span className="text-teal-700 font-semibold">{s.correction}</span>
                          <span className="text-zinc-400">{s.after}…</span>
                          {canJump && s.start != null && (
                            <span className="ml-1 text-blue-700 not-italic">
                              <Play size={9} weight="fill" className="inline" />
                            </span>
                          )}
                        </button>
                      ))
                    )}
                  </li>
                );
              })}
            </ul>
            {submitting && (
              <div className="text-xs text-zinc-600 flex items-center gap-2" data-testid="pending-corrections-progress">
                <SpinnerGap size={12} className="animate-spin" />
                {progress.done + progress.ko} / {checked.size}
                {progress.ko > 0 && <span className="text-rose-600 font-semibold">· {progress.ko} erreur(s)</span>}
              </div>
            )}
          </div>
        ) : (
          /* Bogue 1180 — Corps scrollable et borne en largeur : les boutons du
             pied de page doivent rester atteignables quelle que soit la
             longueur des corrections. */
          <div className="space-y-2 min-w-0 flex-1 overflow-y-auto overflow-x-hidden">
            {/* Vincent B941 (Eric 2026-02-14 SPEC STRICK PROD) — Filtres :
                longueur mini, occurrences mini, regex sur original. */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 min-w-0" data-testid="pending-corrections-filters">
              <label className="text-[10px] text-zinc-600 flex flex-col gap-0.5 min-w-0">
                <span>{lang === "fr" ? "Longueur min" : "Min length"}</span>
                <input
                  type="number"
                  min={0}
                  max={30}
                  value={minLen}
                  onChange={(e) => setMinLen(Math.max(0, Number(e.target.value) || 0))}
                  disabled={submitting}
                  className="text-xs px-1.5 py-0.5 border border-zinc-300 rounded w-full min-w-0"
                  data-testid="pending-corrections-filter-min-len"
                />
              </label>
              <label className="text-[10px] text-zinc-600 flex flex-col gap-0.5 min-w-0">
                <span>{lang === "fr" ? "Occurrences min" : "Min occurrences"}</span>
                <input
                  type="number"
                  min={1}
                  max={999}
                  value={minOcc}
                  onChange={(e) => setMinOcc(Math.max(1, Number(e.target.value) || 1))}
                  disabled={submitting}
                  className="text-xs px-1.5 py-0.5 border border-zinc-300 rounded w-full min-w-0"
                  data-testid="pending-corrections-filter-min-occ"
                />
              </label>
              <label className="text-[10px] text-zinc-600 flex flex-col gap-0.5 min-w-0">
                <span>{lang === "fr" ? "Regex / mot" : "Regex / word"}</span>
                <input
                  type="text"
                  value={pattern}
                  onChange={(e) => setPattern(e.target.value)}
                  disabled={submitting}
                  placeholder={lang === "fr" ? "ex. ^retrai" : "e.g. ^retrai"}
                  className="text-xs px-1.5 py-0.5 border border-zinc-300 rounded font-mono w-full min-w-0"
                  data-testid="pending-corrections-filter-pattern"
                />
              </label>
            </div>
            {/* Vincent B942 (Eric 2026-02-14 SPEC STRICK PROD) — Preview
                des 2-3 segments impactés + contexte avant/après.
                3e passe (2026-06-25) : le panneau s'affiche dès qu'on clique
                « Extraits ». Avant, la condition `previewSamples.length > 0`
                le faisait disparaître en silence — c'est le « il ne se passe
                rien » signalé par Vincent. */}
            {preview && (
              <div
                className="border border-blue-200 bg-blue-50/40 rounded-lg p-2 space-y-1"
                data-testid="pending-corrections-preview"
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold text-blue-900 uppercase tracking-wider">
                    {lang === "fr" ? `Prévisualisation (${previewSamples.length} exemple(s))` : `Preview (${previewSamples.length} sample(s))`}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPreview(null)}
                    className="text-[10px] text-blue-700 hover:underline"
                    data-testid="pending-corrections-preview-close"
                  >
                    ×
                  </button>
                </div>
                {previewSamples.length === 0 && (
                  <p className="text-[11px] text-zinc-600 italic" data-testid="pending-corrections-preview-empty">
                    {lang === "fr"
                      ? "Aucun segment ne porte encore cette correction : elle a probablement déjà été traitée. Cliquez « Rafraîchir » ou rouvrez la fenêtre."
                      : "No segment carries this correction any more: it was probably already handled. Refresh or reopen the window."}
                  </p>
                )}
                {previewSamples.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => jumpToSample(s)}
                    disabled={!canJump || s.start == null}
                    className={`block w-full min-w-0 text-left text-[11px] text-zinc-700 font-mono leading-snug break-words whitespace-normal rounded px-1 -mx-1 ${
                      canJump && s.start != null ? "hover:bg-blue-100 cursor-pointer" : "cursor-default"
                    }`}
                    data-testid={`pending-corrections-preview-sample-${i}`}
                    title={canJump && s.start != null
                      ? (lang === "fr" ? "Écouter ce passage dans la vidéo" : "Listen to this passage in the video")
                      : undefined}
                  >
                    <span className="text-zinc-400">…{s.before}</span>
                    <span className="text-rose-700 line-through">{s.original}</span>
                    <span className="text-zinc-400 mx-0.5">→</span>
                    <span className="text-teal-700 font-semibold">{s.correction}</span>
                    <span className="text-zinc-400">{s.after}…</span>
                    {canJump && s.start != null && (
                      <span className="ml-1 text-blue-700">
                        <Play size={9} weight="fill" className="inline" />
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between text-xs">
              <label className="inline-flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={checked.size === pendingGroups.length && pendingGroups.length > 0}
                  onChange={toggleAll}
                  disabled={submitting}
                  className="h-3.5 w-3.5 accent-[#002FA7]"
                  data-testid="pending-corrections-toggle-all"
                />
                <span>{lang === "fr" ? "Tout cocher / décocher" : "Toggle all"}</span>
              </label>
              <span className="text-zinc-500" data-testid="pending-corrections-count-selected">
                {checked.size} / {pendingGroups.length} {lang === "fr" ? "sélectionnée(s)" : "selected"}
              </span>
            </div>
            <ul
              className="max-h-[min(50vh,380px)] min-w-0 overflow-y-auto overflow-x-hidden border border-zinc-200 rounded-lg divide-y divide-zinc-100"
              data-testid="pending-corrections-list"
            >
              {pendingGroups.map((g) => (
                <li
                  key={g.key}
                  className={`px-3 py-2 flex items-start gap-3 text-sm min-w-0 hover:bg-zinc-50 cursor-pointer ${
                    checked.has(g.key) ? "bg-blue-50/60" : ""
                  }`}
                  onClick={() => !submitting && toggleCheck(g.key)}
                  data-testid={`pending-corrections-row-${g.key}`}
                >
                  <input
                    type="checkbox"
                    checked={checked.has(g.key)}
                    onChange={() => toggleCheck(g.key)}
                    onClick={(e) => e.stopPropagation()}
                    disabled={submitting}
                    className="h-3.5 w-3.5 mt-1 accent-[#002FA7] cursor-pointer flex-shrink-0"
                  />
                  <span className="flex-1 min-w-0 flex flex-col gap-0.5">
                    <span className="flex items-baseline gap-2 flex-wrap min-w-0">
                      <span className="text-rose-700 line-through font-mono text-[12px] break-words min-w-0">{g.original}</span>
                      <span className="text-zinc-400">→</span>
                      <span className="text-teal-700 font-mono text-[12px] font-semibold break-words min-w-0">{g.correction}</span>
                    </span>
                    {/* Bogue 942 (refait) — Extrait TOUJOURS visible : le
                        contexte du premier segment concerné, sans clic.
                        Bogue 1180 : deux lignes maximum, avec retour à la
                        ligne — `truncate` ne bornait rien tant que le parent
                        pouvait s'élargir. */}
                    {(() => {
                      const s = samplesFor(g.original, g.correction, 1)[0];
                      if (!s) return null;
                      return (
                        <span
                          className="text-[10px] text-zinc-500 font-mono leading-snug break-words line-clamp-2 min-w-0"
                          data-testid={`pending-corrections-inline-sample-${g.key}`}
                          title={`…${s.before}${s.original} → ${s.correction}${s.after}…`}
                        >
                          …{s.before}
                          <span className="text-rose-600 line-through">{s.original}</span>
                          <span className="mx-0.5">→</span>
                          <span className="text-teal-700 font-semibold">{s.correction}</span>
                          {s.after}…
                        </span>
                      );
                    })()}
                  </span>
                  <span
                    className="flex-shrink-0 mt-0.5 text-[11px] font-semibold text-zinc-500 tabular-nums px-2 py-0.5 rounded bg-zinc-100"
                    title={lang === "fr" ? "Nombre de segments concernés" : "Segments affected"}
                    data-testid={`pending-corrections-count-${g.key}`}
                  >
                    ×{g.occurrences}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setPreview({ original: g.original, correction: g.correction }); }}
                    className="flex-shrink-0 mt-0.5 text-[10px] text-blue-700 hover:underline inline-flex items-center gap-1"
                    title={lang === "fr" ? "Voir 2-3 extraits avec leur contexte" : "See 2-3 extracts with context"}
                    data-testid={`pending-corrections-preview-btn-${g.key}`}
                  >
                    <Eye size={13} />
                    {lang === "fr" ? "Extraits" : "Extracts"}
                  </button>
                </li>
              ))}
            </ul>
            {submitting && (
              <div className="text-xs text-zinc-600 flex items-center gap-2" data-testid="pending-corrections-progress">
                <SpinnerGap size={12} className="animate-spin" />
                {progress.done + progress.ko} / {checked.size}
                {progress.ko > 0 && <span className="text-rose-600 font-semibold">· {progress.ko} erreur(s)</span>}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex-shrink-0 flex-wrap gap-2">
          {confirmStep ? (
            <>
              <Button
                variant="outline"
                onClick={() => setConfirmStep(null)}
                disabled={submitting}
                data-testid="pending-corrections-confirm-back"
              >
                <ArrowLeft size={14} className="mr-1" />
                {lang === "fr" ? "Retour à la liste" : "Back to list"}
              </Button>
              <Button
                onClick={() => bulkApply(confirmStep)}
                disabled={submitting || selectedGroups.length === 0}
                className={confirmStep === "accept"
                  ? "bg-teal-600 hover:bg-teal-700 text-white"
                  : "bg-rose-600 hover:bg-rose-700 text-white"}
                data-testid="pending-corrections-confirm-apply"
              >
                {submitting
                  ? <SpinnerGap size={14} className="mr-1 animate-spin" />
                  : confirmStep === "accept"
                    ? <CheckCircle size={14} className="mr-1" />
                    : <XCircle size={14} className="mr-1" />}
                {confirmStep === "accept"
                  ? (lang === "fr" ? `Confirmer l'acceptation (${selectedGroups.length})` : `Confirm accept (${selectedGroups.length})`)
                  : (lang === "fr" ? `Confirmer le rejet (${selectedGroups.length})` : `Confirm reject (${selectedGroups.length})`)}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="pending-corrections-close">
                {lang === "fr" ? "Fermer" : "Close"}
              </Button>
              <Button
                variant="outline"
                onClick={() => setConfirmStep("reject")}
                disabled={checked.size === 0 || submitting}
                className="border-rose-300 text-rose-700 hover:bg-rose-50"
                data-testid="pending-corrections-reject"
              >
                <XCircle size={14} className="mr-1" />
                {lang === "fr" ? `Rejeter ${checked.size || ""}` : `Reject ${checked.size || ""}`}
              </Button>
              <Button
                onClick={() => setConfirmStep("accept")}
                disabled={checked.size === 0 || submitting}
                className="bg-teal-600 hover:bg-teal-700 text-white"
                data-testid="pending-corrections-accept"
              >
                <CheckCircle size={14} className="mr-1" />
                {lang === "fr" ? `Accepter ${checked.size || ""}` : `Accept ${checked.size || ""}`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PendingCorrectionsModal;
